import React, { useState, useMemo } from 'react';
import { useAppContext } from '../AppContext';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions'; // Import httpsCallable
import { useTranslation } from 'react-i18next';

const GiftTicketModal = ({ onClose }) => {
    const { userConnections, userProfiles, appFunctions, setMessage } = useAppContext();
    const { t } = useTranslation();
    const [recipientId, setRecipientId] = useState('');
    const [amount, setAmount] = useState(10);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Create an enriched list of connections that includes their profile information
    const friendProfiles = useMemo(() => {
        if (!userConnections || !userProfiles) return [];
        return userConnections
            .map(conn => userProfiles.find(p => p.id === conn.id))
            .filter(Boolean); // Filter out any connections for whom a profile hasn't loaded yet
    }, [userConnections, userProfiles]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!recipientId) {
            setMessage(t('giftTicketModal.selectFriendError'), 'error');
            return;
        }
        setIsSubmitting(true);
        // Correctly create the callable function
        const giftLotteryTicket = httpsCallable(appFunctions, 'giftLotteryTicket');
        try {
            await giftLotteryTicket({ recipientId, amount });
            setMessage(t('giftTicketModal.giftSuccess', { amount }), 'success');
            onClose();
        } catch (err) {
            console.error(t('giftTicketModal.giftErrorLog'), err);
            setMessage(err.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-gray-900/80 p-6 rounded-xl shadow-glow max-w-md w-full border border-cyan-500/50" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-white font-playfair">{t('giftTicketModal.title')}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><LucideIcons.X size={24} /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="recipient" className="block text-sm font-medium text-gray-300 mb-1">{t('giftTicketModal.selectFriendLabel')}</label>
                        <select 
                            id="recipient"
                            value={recipientId}
                            onChange={(e) => setRecipientId(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white"
                        >
                            <option value="">{t('giftTicketModal.selectConnectionPlaceholder')}</option>
                            {friendProfiles.map(profile => (
                                <option key={profile.id} value={profile.id}>
                                    {profile.displayName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-300 mb-1">{t('giftTicketModal.amountLabel')}</label>
                        <input 
                            type="number"
                            id="amount"
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            min="1"
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white"
                        />
                    </div>
                    <div className="pt-2">
                        <button type="submit" disabled={isSubmitting || !recipientId} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50">
                            {isSubmitting ? t('giftTicketModal.sending') : t('giftTicketModal.sendButton', { amount })}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GiftTicketModal;